import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { PageSkeleton } from "@/components/PageSkeleton";
import { useGlobalLoading } from "@/contexts/GlobalLoadingContext";
import { cn } from "@/lib/utils";

/** How long every screen shows its skeleton before its content mounts. */
export const ROUTE_SKELETON_MS = 1000;

/**
 * Holds a route's content behind the skeleton for a fixed beat, however
 * fast the page itself is.
 *
 * Every screen then opens the same way instead of some snapping in and
 * others crawling, which is what makes navigation feel uneven. The beat
 * is deliberate, not a measurement: the content is not mounted until it
 * passes, so it costs that time on top of whatever the page needs.
 */
export function RouteHold({ children, className }: { children: ReactNode; className?: string }) {
  const { pathname } = useLocation();
  const [readyPath, setReadyPath] = useState<string | null>(null);
  const ready = readyPath === pathname;

  useEffect(() => {
    const timer = setTimeout(() => setReadyPath(pathname), ROUTE_SKELETON_MS);
    return () => clearTimeout(timer);
  }, [pathname]);

  // The bar reports the same window the skeleton covers.
  useGlobalLoading(!ready);

  if (!ready) {
    return (
      <div className={cn("min-h-0 flex-1 overflow-y-auto", className)}>
        <PageSkeleton className="p-4 md:p-6" />
      </div>
    );
  }
  return <>{children}</>;
}
